/**
 * The camera's story for the galaxy chapter, as pure numbers: the entry dolly, then an orbit around the bowl.
 * Z is up; the galaxy lies in the x-y plane.
 */

export type Vec3 = readonly [number, number, number];

export const DOLLY_SECONDS = 12;
/** after the dolly the look-at point eases from the dolly's end target to the orbit target over this long */
export const LOOK_SETTLE_SECONDS = 2;
export const ORBIT_TARGET: Vec3 = [0, 0, -3];
export const DOLLY_FROM = { pos: [-34, -98, 62] as Vec3, look: [0, 0, 3] as Vec3 };
export const DOLLY_TO = { pos: [10, -58, 17] as Vec3, look: [0, 0, -4] as Vec3 };
export const EL_MIN = 0.05;
export const EL_MAX = 1.4;
export const RADIUS_MIN = 20;
export const RADIUS_MAX = 300;

export const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

export function smoothstep01(t: number): number {
  const u = clamp(t, 0, 1);
  return u * u * (3 - 2 * u);
}

function lerp3(a: Vec3, b: Vec3, t: number): [number, number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** Camera position and look-at point `seconds` after the chapter begins. */
export function dollyPose(seconds: number): { pos: [number, number, number]; look: [number, number, number] } {
  const s = smoothstep01(seconds / DOLLY_SECONDS);
  const pos = lerp3(DOLLY_FROM.pos, DOLLY_TO.pos, s);
  const look = seconds <= DOLLY_SECONDS
    ? lerp3(DOLLY_FROM.look, DOLLY_TO.look, s)
    : lerp3(DOLLY_TO.look, ORBIT_TARGET, smoothstep01((seconds - DOLLY_SECONDS) / LOOK_SETTLE_SECONDS));
  return { pos, look };
}

export interface Spherical { az: number; el: number; radius: number; }

/** Azimuth (about +z), elevation (above the x-y plane) and radius of `pos` as seen from `target`. */
export function toSpherical(pos: Vec3, target: Vec3 = ORBIT_TARGET): Spherical {
  const dx = pos[0] - target[0], dy = pos[1] - target[1], dz = pos[2] - target[2];
  const h = Math.hypot(dx, dy);
  return { az: Math.atan2(dy, dx), el: Math.atan2(dz, h), radius: Math.hypot(h, dz) };
}

export function fromSpherical(target: Vec3, az: number, el: number, radius: number): [number, number, number] {
  const ce = Math.cos(el);
  return [target[0] + radius * ce * Math.cos(az), target[1] + radius * ce * Math.sin(az), target[2] + radius * Math.sin(el)];
}

export const clampElevation = (el: number) => clamp(el, EL_MIN, EL_MAX);
export const clampRadius = (r: number) => clamp(r, RADIUS_MIN, RADIUS_MAX);
